// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { OffsetOptions } from "@floating-ui/dom";

import { useId } from "@floating-ui/react";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import FancyScroll, { ScrollIndicator } from "@vanmeter/App/Components/Common/FancyScroll";
import FloatingUiMenu, { MenuItem } from "@vanmeter/App/Components/Menu/DropdownMenu";
import MenuCategoryInfo from "@vanmeter/MobiusOverrides/Menu/MenuCategoryInfo";
import { CategoryPropertiesTypes } from "@vanmeter/XDL/types";
import { color } from "@vanmeter/XDL/Utilities";
import React, { FC, memo, useCallback, useRef } from "react";
import styled, { css, ThemeProps, useTheme } from "styled-components";

export type MenuVariants = "primary" | "secondary";

const DEFAULT_BOUNDARY_SELECTOR = "#mainNavigationContainer > div";

// </XNG-Changes>
const fancyScrollExtendedStyles = {
    css: css`
        height: 100%;
        border-top: 3px solid ${color("common.ui8")};
        & > ${ScrollIndicator} {
            height: 36px;
        }
    `,
};
// </XNG-Changes>

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
    // XNG-Changes
    properties?: CategoryPropertiesTypes;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement<{ id: string; className: string }>;
        /**
         * @deprecated Use CSS style to control width.
         * * Width of the menu and submenus in pixels.
         */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /**
         * XNG-Changes
         * Choose 'Primary' when the subMenu needs to match the parent menu height.
         */
        menuVariant?: MenuVariants;
        /**
         * menu item index
         */
        referenceIndex?: number;
        offset?: OffsetOptions;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps & MenuPresentationProps;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        /**
         * XNG-Changes
         */
        referenceIndex?: number;
    };

const MenuStyle = styled.ul<MenuStyleProps>`
    display: flex;
    flex-direction: column;
    background: ${getColor("common.white")};
    z-index: ${getProp("theme.zIndex.menu")};
    width: ${getProp("width", 445)}px;
    height: 100%;
    list-style: none;
    margin: 0;

    // Change background for active item in Menu
    & > .MenuItem[data-open] {
        background: ${getColor("secondary")};
    }

    ${injectCss}
`;

type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick?: () => void;
    };

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    position: relative;
    a {
        width: 100%;
        justify-content: space-between;
        &:hover {
            background: ${getColor("common.border")};
        }
    }
    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    padding: 0;
    color: ${getColor("text.main")};
    display: block;
    box-sizing: border-box;
`;

const RenderMenuItems: FC<{
    childMenuItems: MappedLink[];
    currentDepth: number;
    maxDepth: number;
    width?: number;
    applyProp: any;
    applyStyledProp: any;
    resolvedMergeCss: boolean | undefined;
    displayChangeCustomerLink?: boolean;
    renderMenuItems: (items: MappedLink[], depth: number) => React.ReactNode;
    menuVariant?: MenuVariants;
}> = memo(
    ({
        childMenuItems,
        currentDepth,
        maxDepth,
        width,
        applyProp,
        applyStyledProp,
        resolvedMergeCss = true,
        displayChangeCustomerLink,
        renderMenuItems,
        menuVariant,
    }) => {
        const id = useId();
        const menuStyleRef = useRef<HTMLUListElement>(null);
        return (
            <React.Fragment key={id}>
                {childMenuItems
                    ?.filter(item => !item.excludeFromNavigation)
                    .map((item, index) => {
                        const hasChildren =
                            item.children?.some(o => !o.excludeFromNavigation) && currentDepth < maxDepth;

                        if (item.type === "ChangeCustomerPage" && !displayChangeCustomerLink) {
                            return null;
                        }

                        const itemElement = (
                            <MenuItem
                                targetComponent={
                                    <MenuItemStyle
                                        width={width}
                                        css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
                                        key={`${item.title}_${index}`}
                                        data-test-selector="menuItem"
                                    >
                                        <Clickable
                                            href={item.url}
                                            target={item.openInNewWindow ? "_blank" : ""}
                                            {...applyProp("menuItemClickableProps")}
                                            {...item.clickableProps}
                                        >
                                            {item.iconProps && <IconMemo {...item.iconProps} />}
                                            <MenuItemText
                                                hasChildren={hasChildren}
                                                {...applyProp("menuItemTypographyProps")}
                                                data-test-selector="menuItemText"
                                            >
                                                {item.title}
                                            </MenuItemText>
                                            {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
                                        </Clickable>
                                    </MenuItemStyle>
                                }
                            />
                        );

                        if (hasChildren) {
                            return (
                                <FloatingUiMenu
                                    key={`FloatingUiMenu_${item.title}_${index}`}
                                    menuTrigger={itemElement}
                                    offset={0}
                                    placement="right-start"
                                    shift={{
                                        boundary:
                                            typeof document !== "undefined"
                                                ? document.querySelector(DEFAULT_BOUNDARY_SELECTOR) || document.body
                                                : "clippingAncestors",
                                        padding: 16,
                                    }}
                                    variant={menuVariant}
                                    delay={{ open: 0, close: 300 }}
                                >
                                    {renderMenuItems(item!.children!, currentDepth + 1)}
                                </FloatingUiMenu>
                            );
                        }
                        const hasCatContent =
                            item.properties?.contentBody ||
                            item.properties?.contentImage ||
                            item.properties?.contentHeading;
                        if (currentDepth >= 2 && hasCatContent) {
                            return (
                                <FloatingUiMenu
                                    key={`FloatingUiMenu_${item.title}_${index}`}
                                    menuTrigger={itemElement}
                                    offset={0}
                                    placement="right-start"
                                    shift={{
                                        boundary:
                                            typeof document !== "undefined"
                                                ? document.querySelector(DEFAULT_BOUNDARY_SELECTOR) || document.body
                                                : "clippingAncestors",
                                        padding: 16,
                                    }}
                                    variant={menuVariant}
                                    forceParentHeight={true}
                                    delay={{ open: 0, close: 300 }}
                                >
                                    <FancyScroll refElement={menuStyleRef} extendedStyles={fancyScrollExtendedStyles}>
                                        <MenuStyle
                                            width={width}
                                            ref={menuStyleRef}
                                            css={css`
                                                ${applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                            `}
                                        >
                                            <MenuCategoryInfo
                                                contentBody={item.properties?.contentBody}
                                                contentButtonLink={item.properties?.contentButtonLink}
                                                contentButtonText={item.properties?.contentButtonText}
                                                contentHeading={item.properties?.contentHeading}
                                                contentImage={item.properties?.contentImage}
                                            />
                                        </MenuStyle>
                                    </FancyScroll>
                                </FloatingUiMenu>
                            );
                        }

                        return <React.Fragment key={`MenuItem_${item.title}_${id}`}>{itemElement}</React.Fragment>;
                    })}
            </React.Fragment>
        );
    },
);

const Menu: FC<MenuProps> = ({
    menuItems,
    menuTrigger,
    isOpen,
    mergeCss,
    menuVariant,
    referenceIndex,
    offset,
    ...otherProps
}) => {
    // const menuStyleRef = useRef<HTMLUListElement>(null);
    const theme = useTheme() as BaseTheme;
    const { applyProp, applyStyledProp } = applyPropBuilder({ ...otherProps, theme }, { component: "menu" });
    const resolvedMergeCss = mergeCss ?? theme?.menu?.defaultProps?.mergeCss;
    const { maxDepth = 3, width, displayChangeCustomerLink } = otherProps;

    const renderMenuItems = useCallback(
        (childMenuItems: MappedLink[], currentDepth: number) => {
            // XNG-Changes: move menuStyleRef here
            const menuStyleRef = useRef<HTMLUListElement>(null);
            return (
                <FancyScroll refElement={menuStyleRef} extendedStyles={fancyScrollExtendedStyles}>
                    <MenuStyle
                        ref={menuStyleRef}
                        width={otherProps.width}
                        referenceIndex={referenceIndex}
                        css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                    >
                        <RenderMenuItems
                            childMenuItems={childMenuItems}
                            currentDepth={currentDepth}
                            width={width}
                            maxDepth={maxDepth}
                            applyProp={applyProp}
                            applyStyledProp={applyStyledProp}
                            resolvedMergeCss={resolvedMergeCss}
                            displayChangeCustomerLink={displayChangeCustomerLink}
                            renderMenuItems={renderMenuItems}
                            menuVariant={menuVariant}
                        />
                    </MenuStyle>
                </FancyScroll>
            );
        },

        [applyStyledProp, resolvedMergeCss, width, displayChangeCustomerLink, maxDepth],
    );

    return (
        <FloatingUiMenu
            menuTrigger={menuTrigger}
            overlay={true}
            offset={offset || { mainAxis: 0 }}
            placement="bottom-start"
            shift={{
                boundary:
                    typeof document !== "undefined"
                        ? document.querySelector(DEFAULT_BOUNDARY_SELECTOR) || document.body
                        : "clippingAncestors",
                padding: 16,
            }}
            variant={menuVariant}
        >
            {renderMenuItems(menuItems, 1)}
        </FloatingUiMenu>
    );
};

/** @component */
export default Menu;

export { MenuItemText };
